import React from 'react';

import { Remove as RemoveIcon } from '@mui/icons-material';
import { Box, Divider, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { isNotNil } from 'es-toolkit';
import { DateTime, Interval } from 'luxon';

import { DateFormatPatterns } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';
import { numberWithCommas } from '@clearverify/utils';

import { DashboardWorkLetterUsageItem } from 'src/api';
import { ChartBase, ChartOptions } from 'src/components/chart';
import { ButtonDatePicker } from 'src/components/common';
import { DashboardPalette } from 'src/constants/dashboard';
import { useDashboardPrintView } from 'src/hooks/dashboard';
import { snapToUtcMonthEnd, snapToUtcMonthStart } from 'src/utils/dashboard';

import { NOW } from '../constants';
import { DashboardChartWrapper } from '../DashboardChartWrapper';

import {
  WORK_LETTER_TYPE_TO_COLOR_MAP,
  WORK_LETTER_TYPE_TO_TITLE_MAP,
  WORK_LETTER_TYPES,
} from './constants';

export type DashboardWorkLetterUsageBlockProps = {
  dashboardWorkLetterUsageItems: DashboardWorkLetterUsageItem[];
  startDate: Nullable<DateTime>;
  endDate: Nullable<DateTime>;
  onStartDateChange?: (startDate: Nullable<DateTime>) => void;
  onEndDateChange?: (endDate: Nullable<DateTime>) => void;
  minDate?: Nullable<DateTime>;
  maxDate?: Nullable<DateTime>;
  loading?: boolean;
};

export const DashboardWorkLetterUsageBlock: React.FC<DashboardWorkLetterUsageBlockProps> = ({
  dashboardWorkLetterUsageItems,
  onStartDateChange,
  onEndDateChange,
  startDate,
  endDate,
  minDate = NOW.minus({ months: 6 }),
  maxDate = NOW,
  loading,
}) => {
  const { isPrintView } = useDashboardPrintView();

  const handleStartDateChange = React.useCallback(
    (date: Nullable<DateTime>) => onStartDateChange?.(snapToUtcMonthStart(date)),
    [onStartDateChange],
  );

  const handleEndDateChange = React.useCallback(
    (date: Nullable<DateTime>) => onEndDateChange?.(snapToUtcMonthEnd(date)),
    [onEndDateChange],
  );

  const interval = startDate && endDate ? Interval.fromDateTimes(startDate, endDate) : undefined;

  const months = interval
    ?.splitBy({ months: 1 })
    .map(interval => interval.start?.toFormat(DateFormatPatterns.fullMonthYear))
    .filter(isNotNil);

  const isDatasetEmpty = !dashboardWorkLetterUsageItems.length && !loading;

  const options = React.useMemo(
    (): ChartOptions => ({
      grid: { top: 10, right: 40, bottom: 55, left: 40 },
      animationDuration: isPrintView ? 0 : undefined,
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: {
          fontSize: 14,
          fontWeight: 'bold',
        },
      },
      yAxis: {
        type: 'value',
        position: 'right',
        axisLabel: {
          fontSize: 14,
          fontWeight: 'bold',
        },
      },
      legend: {
        show: true,
        bottom: 0,
        textStyle: {
          fontSize: 13,
        },
      },
      tooltip: {
        trigger: 'axis',
        valueFormatter: value => numberWithCommas(Number(value?.valueOf())),
      },
      series: WORK_LETTER_TYPES.map(type => ({
        name: WORK_LETTER_TYPE_TO_TITLE_MAP[type],
        datasetId: type,
        type: 'bar',
        encode: {
          x: 'month',
          y: 'count',
        },
        label: isPrintView ? LABEL_OPTIONS : {},
        itemStyle: {
          borderRadius: 5,
        },
        color: loading ? DashboardPalette.slate50 : WORK_LETTER_TYPE_TO_COLOR_MAP[type],
      })),
      dataset: [
        {
          id: 'dataset_raw',
          dimensions: ['month', 'count', 'type'],
          source: dashboardWorkLetterUsageItems,
        },
        ...WORK_LETTER_TYPES.map(type => ({
          id: type,
          fromDatasetId: 'dataset_raw',
          transform: [
            {
              type: 'filter',
              config: {
                dimension: 'type',
                '=': type,
              },
            },
          ],
        })),
      ],
    }),
    [dashboardWorkLetterUsageItems, isPrintView, loading, months],
  );

  return (
    <Paper sx={{ py: 2.5, px: 3, borderRadius: 2, overflow: 'hidden' }} variant="outlined">
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle1">Work Letter Usage</Typography>
          <Typography variant="body1" color="text.secondary">
            Work letter usage reports allow you to track employment and immigration letter activity
            across an adjustable period of time for your employee base.
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please note: Each generated work letter is counted as a separate request. If an employee
            generates more than one letter of the same type, each appears in the totals for
            Employment, Employment with Income, and Immigration letters.
          </Typography>
        </Box>
        <Box height="4px">{loading ? <LinearProgress color="secondary" /> : <Divider />}</Box>

        <Stack direction="row" alignItems="center" spacing={1}>
          <ButtonDatePicker
            views={['month', 'year']}
            timezone="UTC"
            minDate={minDate || undefined}
            maxDate={endDate || maxDate || undefined}
            value={startDate}
            onChange={handleStartDateChange}
            disabled={loading}
          />
          <Box display="flex" alignItems="center" fontSize="13px">
            <RemoveIcon fontSize="inherit" />
          </Box>
          <ButtonDatePicker
            views={['month', 'year']}
            timezone="UTC"
            minDate={minDate || undefined}
            maxDate={maxDate || undefined}
            value={endDate}
            onChange={handleEndDateChange}
            disabled={loading}
          />
        </Stack>

        {Boolean(months?.length) && (
          <DashboardChartWrapper
            isDatasetEmpty={isDatasetEmpty}
            isPrintView={isPrintView}
            wrapperHeight={555}
          >
            <ChartBase option={options} style={{ height: 555 }} />
          </DashboardChartWrapper>
        )}
      </Stack>
    </Paper>
  );
};

const LABEL_OPTIONS = {
  show: true,
  fontSize: '16px',
  fontWeight: 'bold' as const,
  formatter: '{@[1]}',
  position: 'inside' as const,
  offset: [60, 0],
  rotate: 90,
  inside: false,
};
